// Gear: bases, rarities, gold chests, rolls and save validation. Pure data, no three.js: node tests load it directly.
import type { PlayerMods } from './content.ts';

export type GearSlot = 'staff' | 'hat' | 'amulet' | 'robe' | 'ring' | 'boots';
export const GEAR_SLOTS: GearSlot[] = ['staff', 'hat', 'amulet', 'robe', 'ring', 'boots'];

export type GearStat =
  | 'might' | 'crit' | 'haste' | 'area' | 'duration' | 'maxHp' | 'armor'
  | 'regen' | 'moveSpeed' | 'magnet' | 'growth' | 'greed' | 'luck';

export type GearSetId = 'ember' | 'storm' | 'verdant' | 'void';
export type GearPerkId = 'arc_pulse' | 'scholar_crown' | 'sanctified_hearth' | 'aegis_pulse' | 'alchemist_loop' | 'stormstep';

export interface GearSetBonus {
  pieces: 2 | 4;
  stats: Partial<Record<GearStat, number>>;
}

export interface GearSetDef {
  id: GearSetId;
  color: string;
  bonuses: [GearSetBonus, GearSetBonus];
}

export interface GearBase {
  id: string;
  slot: GearSlot;
  set: GearSetId;
  stats: [GearStat, GearStat, GearStat]; // a rarity with n stats uses the first n
}

export interface RarityDef {
  id: 'common' | 'rare' | 'epic' | 'legendary';
  stats: number;
  factor: number;
  salvage: number;
  color: string;
}

export type ChestId = 'bronze' | 'silver' | 'gold';

export interface Guarantee {
  rarity: number;
  within: number;
}

export interface ChestDef {
  id: ChestId;
  price: number;
  odds: [number, number, number, number]; // common, rare, epic, legendary
  guarantees: Guarantee[];
}

export interface GearItem {
  id: string;
  base: string;
  rarity: number;
  q: number[]; // one roll in [0, 1] per active stat
  level: number;
}

export interface GearState {
  items: GearItem[];
  equipped: Partial<Record<GearSlot, string>>;
  pity: Partial<Record<ChestId, number[]>>;
}

export const GEAR_CAP = 60;
export const MAX_GEAR_LEVEL = 10;
export const ENHANCE_BASE = [30, 60, 120, 200];

export const RARITIES: RarityDef[] = [
  { id: 'common', stats: 1, factor: 0.4, salvage: 30, color: '#b8c0cc' },
  { id: 'rare', stats: 2, factor: 0.6, salvage: 90, color: '#5ea8ff' },
  { id: 'epic', stats: 3, factor: 0.8, salvage: 250, color: '#b77bff' },
  { id: 'legendary', stats: 3, factor: 1.0, salvage: 600, color: '#ffc23d' },
];

// Legendary value at level 1 and a perfect roll (0.7x so six level-1 slots equal four old slots).
export const STAT_CAPS: Record<GearStat, number> = {
  might: 0.07, crit: 0.035, haste: 0.042, area: 0.042, duration: 0.056, maxHp: 14, armor: 0.7,
  regen: 0.14, moveSpeed: 0.042, magnet: 0.105, growth: 0.056, greed: 0.14, luck: 0.042,
};

export const GEAR_SETS: GearSetDef[] = [
  {
    id: 'ember',
    color: '#ff7847',
    bonuses: [
      { pieces: 2, stats: { maxHp: 15, moveSpeed: 0.05 } },
      { pieces: 4, stats: { might: 0.08, crit: 0.05 } },
    ],
  },
  {
    id: 'storm',
    color: '#5fc8ff',
    bonuses: [
      { pieces: 2, stats: { moveSpeed: 0.06, magnet: 0.12 } },
      { pieces: 4, stats: { haste: 0.07, crit: 0.04 } },
    ],
  },
  {
    id: 'verdant',
    color: '#6be38a',
    bonuses: [
      { pieces: 2, stats: { maxHp: 18, regen: 0.3 } },
      { pieces: 4, stats: { growth: 0.12, greed: 0.15 } },
    ],
  },
  {
    id: 'void',
    color: '#b97aff',
    bonuses: [
      { pieces: 2, stats: { armor: 1.0, luck: 0.06 } },
      { pieces: 4, stats: { area: 0.08, duration: 0.1 } },
    ],
  },
];

export const SLOT_PERKS: Record<GearSlot, GearPerkId> = {
  staff: 'arc_pulse',
  hat: 'scholar_crown',
  amulet: 'sanctified_hearth',
  robe: 'aegis_pulse',
  ring: 'alchemist_loop',
  boots: 'stormstep',
};

export const GEAR_BASES: GearBase[] = [
  { id: 'ember_staff', slot: 'staff', set: 'ember', stats: ['might', 'crit', 'haste'] },
  { id: 'thorn_wand', slot: 'staff', set: 'verdant', stats: ['crit', 'might', 'haste'] },
  { id: 'storm_rod', slot: 'staff', set: 'storm', stats: ['haste', 'might', 'crit'] },
  { id: 'void_scepter', slot: 'staff', set: 'void', stats: ['might', 'area', 'crit'] },
  { id: 'warded_robe', slot: 'robe', set: 'storm', stats: ['maxHp', 'armor', 'regen'] },
  { id: 'moss_robe', slot: 'robe', set: 'verdant', stats: ['regen', 'maxHp', 'armor'] },
  { id: 'iron_mantle', slot: 'robe', set: 'void', stats: ['armor', 'maxHp', 'regen'] },
  { id: 'shadow_cloak', slot: 'robe', set: 'ember', stats: ['maxHp', 'moveSpeed', 'armor'] },
  { id: 'glass_charm', slot: 'amulet', set: 'void', stats: ['area', 'duration', 'growth'] },
  { id: 'hourglass_pendant', slot: 'amulet', set: 'storm', stats: ['duration', 'area', 'luck'] },
  { id: 'sage_locket', slot: 'amulet', set: 'verdant', stats: ['growth', 'luck', 'area'] },
  { id: 'star_talisman', slot: 'amulet', set: 'ember', stats: ['luck', 'growth', 'duration'] },
  { id: 'sun_ring', slot: 'ring', set: 'verdant', stats: ['moveSpeed', 'greed', 'magnet'] },
  { id: 'lode_band', slot: 'ring', set: 'void', stats: ['magnet', 'moveSpeed', 'greed'] },
  { id: 'gilded_ring', slot: 'ring', set: 'ember', stats: ['greed', 'magnet', 'crit'] },
  { id: 'hawk_ring', slot: 'ring', set: 'storm', stats: ['crit', 'moveSpeed', 'magnet'] },
  { id: 'pointed_hat', slot: 'hat', set: 'void', stats: ['haste', 'might', 'growth'] },
  { id: 'storm_hood', slot: 'hat', set: 'storm', stats: ['crit', 'haste', 'area'] },
  { id: 'ember_circlet', slot: 'hat', set: 'ember', stats: ['might', 'area', 'duration'] },
  { id: 'scholar_cap', slot: 'hat', set: 'verdant', stats: ['growth', 'luck', 'haste'] },
  { id: 'wind_striders', slot: 'boots', set: 'storm', stats: ['moveSpeed', 'magnet', 'regen'] },
  { id: 'iron_greaves', slot: 'boots', set: 'void', stats: ['armor', 'moveSpeed', 'maxHp'] },
  { id: 'ember_treads', slot: 'boots', set: 'ember', stats: ['moveSpeed', 'might', 'magnet'] },
  { id: 'moss_sandals', slot: 'boots', set: 'verdant', stats: ['regen', 'moveSpeed', 'greed'] },
];

export const CHESTS: ChestDef[] = [
  { id: 'bronze', price: 150, odds: [0.7, 0.25, 0.05, 0], guarantees: [{ rarity: 1, within: 4 }] },
  { id: 'silver', price: 450, odds: [0.25, 0.5, 0.2, 0.05], guarantees: [{ rarity: 2, within: 8 }, { rarity: 3, within: 40 }] },
  { id: 'gold', price: 1200, odds: [0, 0.35, 0.45, 0.2], guarantees: [{ rarity: 3, within: 10 }] },
];

// The gear shop: a chosen piece costs more than the same rarity from chests. No commons, or four of them would make
// a 4-piece set bonus pocket change.
const SHOP_PRICES = [null, 600, 2500, 8000];

export function gearPrice(rarity: number): number | null {
  return Number.isInteger(rarity) ? (SHOP_PRICES[rarity] ?? null) : null;
}

export const emptyGear = (): GearState => ({ items: [], equipped: {}, pity: {} });

export const gearBase = (id: string): GearBase | undefined => GEAR_BASES.find((b) => b.id === id);

export const gearSet = (id: GearSetId): GearSetDef => GEAR_SETS.find((s) => s.id === id)!;

export function itemPerk(item: GearItem): GearPerkId | null {
  if (item.rarity < RARITIES.length - 1) return null;
  const base = gearBase(item.base);
  return base ? SLOT_PERKS[base.slot] : null;
}

export function activeSets(gear: GearState): Array<{ set: GearSetDef; count: number; tiers: Array<2 | 4> }> {
  const counts = new Map<GearSetId, number>();
  for (const slot of GEAR_SLOTS) {
    const id = gear.equipped[slot];
    const item = id ? gear.items.find((it) => it.id === id) : undefined;
    const base = item ? gearBase(item.base) : undefined;
    if (base) counts.set(base.set, (counts.get(base.set) ?? 0) + 1);
  }
  const out: Array<{ set: GearSetDef; count: number; tiers: Array<2 | 4> }> = [];
  for (const set of GEAR_SETS) {
    const count = counts.get(set.id) ?? 0;
    if (count <= 0) continue;
    const tiers = set.bonuses.filter((b) => count >= b.pieces).map((b) => b.pieces);
    out.push({ set, count, tiers });
  }
  return out;
}

export function activePerks(gear: GearState): GearPerkId[] {
  const out: GearPerkId[] = [];
  for (const slot of GEAR_SLOTS) {
    const id = gear.equipped[slot];
    const item = id ? gear.items.find((it) => it.id === id) : undefined;
    const perk = item ? itemPerk(item) : null;
    if (perk) out.push(perk);
  }
  return out;
}

export function itemStats(item: GearItem): Array<[GearStat, number]> {
  const base = gearBase(item.base);
  const rarity = RARITIES[item.rarity];
  if (!base || !rarity) return [];
  const mult = 1 + 0.1 * ((item.level ?? 1) - 1);
  return base.stats.slice(0, rarity.stats).map((stat, i) => [stat, STAT_CAPS[stat] * rarity.factor * (0.5 + 0.5 * (item.q[i] ?? 0)) * mult]);
}

export function enhanceCost(item: GearItem): number | null {
  if (item.level >= MAX_GEAR_LEVEL) return null;
  const base = ENHANCE_BASE[item.rarity];
  return base ? base * item.level : null;
}

export function salvageValue(item: GearItem): number {
  const rarity = RARITIES[item.rarity];
  const base = ENHANCE_BASE[item.rarity] ?? 0;
  if (!rarity) return 0;
  const spent = (base * item.level * (item.level - 1)) / 2;
  return rarity.salvage + Math.floor(spent / 2);
}

export function chestPrice(chest: ChestDef, count: 1 | 10): number {
  return count === 10 ? chest.price * 9 : chest.price;
}

export function gearMods(gear: GearState): Partial<PlayerMods> {
  const mods: Partial<PlayerMods> = {};
  for (const id of Object.values(gear.equipped)) {
    const item = gear.items.find((it) => it.id === id);
    if (!item) continue;
    for (const [stat, value] of itemStats(item)) mods[stat] = (mods[stat] ?? 0) + value;
  }
  for (const { set, count } of activeSets(gear)) {
    for (const bonus of set.bonuses) {
      if (count < bonus.pieces) continue;
      for (const [stat, value] of Object.entries(bonus.stats) as Array<[GearStat, number]>) {
        mods[stat] = (mods[stat] ?? 0) + value;
      }
    }
  }
  return mods;
}

const ID_CHARS = '0123456789abcdefghijklmnopqrstuvwxyz';

export function rollItem(chest: ChestDef, rng: () => number, minRarity = 0): GearItem {
  let roll = rng();
  let rarity = 0;
  while (rarity < chest.odds.length - 1 && (roll -= chest.odds[rarity]) >= 0) rarity += 1;
  while (chest.odds[rarity] === 0) rarity -= 1; // float leftovers must never land on a 0% rarity
  rarity = Math.max(rarity, minRarity);
  const base = GEAR_BASES[Math.floor(rng() * GEAR_BASES.length)].id;
  const q = Array.from({ length: RARITIES[rarity].stats }, () => rng());
  const id = Array.from({ length: 8 }, () => ID_CHARS[Math.floor(rng() * ID_CHARS.length)]).join('');
  return { id, base, rarity, q, level: 1 };
}

export function rollChosenItem(base: string, rarity: number, rng: () => number): GearItem {
  const q = Array.from({ length: RARITIES[rarity].stats }, () => rng());
  const id = Array.from({ length: 8 }, () => ID_CHARS[Math.floor(rng() * ID_CHARS.length)]).join('');
  return { id, base, rarity, q, level: 1 };
}

export function pullChest(chest: ChestDef, pity: number[] | undefined, rng: () => number): { item: GearItem; pity: number[] } {
  let forced = 0;
  chest.guarantees.forEach((g, i) => {
    if ((pity?.[i] ?? 0) >= g.within - 1) forced = Math.max(forced, g.rarity);
  });
  const item = rollItem(chest, rng, forced);
  const next = chest.guarantees.map((g, i) => (item.rarity >= g.rarity ? 0 : (pity?.[i] ?? 0) + 1));
  return { item, pity: next };
}

export function pityLeft(chest: ChestDef, pity: number[] | undefined): number[] {
  return chest.guarantees.map((g, i) => Math.max(1, g.within - (pity?.[i] ?? 0)));
}

const REFORGE_COSTS = [60, 140, 320, 700];

export function reforgeCost(item: GearItem): number {
  return REFORGE_COSTS[item.rarity] ?? 60;
}

export function reforgeItem(item: GearItem, rng: () => number): GearItem {
  return {
    ...item,
    q: item.q.map((old) => {
      let r = rng();
      if (Math.abs(r - old) < 0.01) r = (r + 0.37) % 1;
      return r;
    }),
  };
}

export function fuseCandidates(gear: GearState, item: GearItem): string[] {
  if (item.rarity >= RARITIES.length - 1) return [];
  const worn = new Set(Object.values(gear.equipped));
  if (worn.has(item.id)) return [];
  const slot = gearBase(item.base)?.slot;
  if (!slot) return [];
  const out: string[] = [];
  for (const it of gear.items) {
    if (it.id === item.id || worn.has(it.id) || it.rarity !== item.rarity) continue;
    if (gearBase(it.base)?.slot === slot) {
      out.push(it.id);
      if (out.length === 2) break;
    }
  }
  return out;
}

export function fuseItems(gear: GearState, primaryId: string, rng: () => number): { gear: GearState; created: GearItem } | null {
  const primary = gear.items.find((it) => it.id === primaryId);
  if (!primary) return null;
  const cands = fuseCandidates(gear, primary);
  if (cands.length < 2) return null;
  const consumed = new Set([primary.id, cands[0], cands[1]]);
  const second = gear.items.find((it) => it.id === cands[0])!;
  const third = gear.items.find((it) => it.id === cands[1])!;
  const level = Math.max(1, Math.min(MAX_GEAR_LEVEL, Math.floor((primary.level + second.level + third.level) / 3)));
  const rarity = primary.rarity + 1;
  const slot = gearBase(primary.base)!.slot;
  const bases = GEAR_BASES.filter((b) => b.slot === slot);
  const base = bases[Math.floor(rng() * bases.length)].id;
  const q = Array.from({ length: RARITIES[rarity].stats }, () => rng());
  const remaining = gear.items.filter((it) => !consumed.has(it.id));
  let id = '';
  do {
    id = Array.from({ length: 8 }, () => ID_CHARS[Math.floor(rng() * ID_CHARS.length)]).join('');
  } while (remaining.some((it) => it.id === id));
  const created: GearItem = { id, base, rarity, q, level };
  return {
    gear: { ...gear, items: [...remaining, created] },
    created,
  };
}

const STAGE_LOOT_SET: GearSetId[] = ['verdant', 'ember', 'storm', 'void', 'storm', 'void', 'ember', 'verdant', 'ember'];

export function rollBossLoot(stageIndex: number, stormTier: number, rng: () => number): GearItem {
  const setId = STAGE_LOOT_SET[stageIndex] ?? 'ember';
  const bases = GEAR_BASES.filter((b) => b.set === setId);
  const base = bases[Math.min(bases.length - 1, Math.floor(rng() * bases.length))].id;
  const roll = rng();
  const legChance = 0.08 + Math.max(0, stormTier) * 0.05;
  const epicChance = 0.32 + Math.max(0, stormTier) * 0.08;
  const rarity = roll < legChance ? 3 : roll < legChance + epicChance ? 2 : 1;
  const q = Array.from({ length: RARITIES[rarity].stats }, () => rng());
  const id = Array.from({ length: 8 }, () => ID_CHARS[Math.floor(rng() * ID_CHARS.length)]).join('');
  return { id, base, rarity, q, level: 1 };
}

// Saves are user-editable and come from other devices: keep only well-formed items, clamped, at most `cap`.
export function cleanGear(raw: unknown, cap = GEAR_CAP): GearState {
  const out = emptyGear();
  if (!raw || typeof raw !== 'object') return out;
  const { items, equipped, pity } = raw as { items?: unknown; equipped?: unknown; pity?: unknown };
  if (pity && typeof pity === 'object') {
    for (const chest of CHESTS) {
      const arr = (pity as Record<string, unknown>)[chest.id];
      if (Array.isArray(arr) && arr.length === chest.guarantees.length && arr.every((v) => typeof v === 'number' && Number.isInteger(v))) {
        out.pity[chest.id] = (arr as number[]).map((v) => Math.min(1000, Math.max(0, v)));
      }
    }
  }
  if (!Array.isArray(items)) return out;
  const slotOf = new Map<string, GearSlot>();
  for (const it of items) {
    if (out.items.length >= cap) break;
    if (!it || typeof it !== 'object') continue;
    const { id, base, rarity, q, level } = it as Record<string, unknown>;
    if (typeof id !== 'string' || !/^[a-z0-9]{1,16}$/.test(id) || slotOf.has(id)) continue;
    const def = typeof base === 'string' ? gearBase(base) : undefined;
    if (!def || typeof rarity !== 'number' || !Number.isInteger(rarity) || !RARITIES[rarity]) continue;
    if (level !== undefined && (typeof level !== 'number' || !Number.isInteger(level))) continue;
    const lv = level === undefined ? 1 : Math.min(MAX_GEAR_LEVEL, Math.max(1, level));
    const n = RARITIES[rarity].stats;
    if (!Array.isArray(q) || q.length < n || !q.slice(0, n).every((v) => typeof v === 'number' && Number.isFinite(v))) continue;
    out.items.push({ id, base: def.id, rarity, q: (q.slice(0, n) as number[]).map((v) => Math.min(1, Math.max(0, v))), level: lv });
    slotOf.set(id, def.slot);
  }
  if (equipped && typeof equipped === 'object') {
    for (const slot of GEAR_SLOTS) {
      const id = (equipped as Record<string, unknown>)[slot];
      if (typeof id === 'string' && slotOf.get(id) === slot) out.equipped[slot] = id;
    }
  }
  return out;
}
